import { FC, useCallback, useEffect, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";
import { MenuItem, Select } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useMutation } from "react-query";
import _ from "lodash";
import { TextInput } from "../../common/components/TextInput";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { SearchButton } from "../../common/components/SearchButton";
import { fetchGetAllParentCompanies } from "../miscellaneous/fetchGetAllParentCompanies";
import { fetchGetCompaniesByParent } from "../../organization-management/miscellaneous/fetchGetCompaniesByParent";

const PARTICIPANT_TYPES = [
  { value: 1, label: "Time Study Participant" },
  { value: 2, label: "Ops Work Log Participant" },
];

const labelFormControlStyle = {
  fontWeight: "700",
  fontSize: "14px",
  marginRight: "12px",
};

type UserSearchProps = {
  control: Control<FieldValues, any, FieldValues>;
  register: UseFormRegister<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues, FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  reset: UseFormReset<FieldValues>;
  setUserSearch: any;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onAtomReset?: () => void;
};

export const UserManagementSearch: FC<UserSearchProps> = ({
  control,
  register,
  handleSubmit,
  getValues,
  setValue,
  reset,
  setUserSearch,
  setPage,
  onAtomReset,
}) => {
  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleSupperAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "super admin";

  const isRoleAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "admin";

  const showCompanyDropdown = isRoleSupperAdmin || isRoleAdmin;

  const callerParentCompanyId = localStorage.getItem("parentCompanyId") ?? "";
  const callerParentCompanyName = localStorage.getItem("parentCompanyName") ?? "";

  const [parentCompanies, setParentCompanies] = useState<{ id: string; name: string }[]>([]);
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);

  const mutationGetParentCompanies = useMutation(fetchGetAllParentCompanies, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const companies = _.map(res.data.data, (item: any) => ({
          id: item.id,
          name: item.companyName,
        }));
        setParentCompanies(companies);
      }
    },
    onError: () => { },
  });

  const mutationGetOrganizations = useMutation(fetchGetCompaniesByParent, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const orgs = _.map(res.data.data, (item: any) => ({
          id: item.id,
          name: item.companyName,
        }));
        setOrganizations(orgs);
      }
    },
    onError: () => { },
  });

  useEffect(() => {
    if (isRoleSupperAdmin) {
      mutationGetParentCompanies.mutate();
      mutationGetOrganizations.mutate("");
    } else if (isRoleAdmin) {
      setParentCompanies([{ id: callerParentCompanyId, name: callerParentCompanyName }]);
      setValue("parentCompanyId", callerParentCompanyId);
      mutationGetOrganizations.mutate(callerParentCompanyId);
    } else {
      mutationGetOrganizations.mutate(callerParentCompanyId);
    }
  }, []);

  const onSearchSubmit = useCallback((data: any) => {
    const companyName = getValues("companyName");
    const fullName = getValues("fullName");
    const role = getValues("role");
    const parentCompanyId = getValues("parentCompanyId");
    const participantType = getValues("participantType");

    setUserSearch({ companyName, fullName, role, parentCompanyId: parentCompanyId ?? "", participantType: participantType ?? "" });
    setPage(0);
  }, []);

  return (
    <div className="mt-9 border-2 pt-7 pb-7">
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        <div className="flex flex-row flex-wrap gap-4">
          {showCompanyDropdown && (
            <div className="flex flex-col w-[32%]">
              <div className="flex items-center">
                <label className="mr-4 w-[30%] shrink-0 text-right font-bold">Company</label>
                <Controller
                  control={control}
                  name="parentCompanyId"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="parentCompanyId"
                      value={value ?? ""}
                      onChange={(e) => {
                        const selectedId = e.target.value as string;
                        setValue("parentCompanyId", selectedId);
                        setValue("companyName", "");
                        mutationGetOrganizations.mutate(selectedId || undefined);
                      }}
                      displayEmpty
                      style={{ height: "38px", width: "240px" }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {parentCompanies.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col w-[32%]">
            <div className="flex items-center">
              <label className="mr-4 w-[30%] shrink-0 text-right font-bold">Organization</label>
              <Controller
                control={control}
                name="companyName"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="companyName"
                    value={value ?? ""}
                    onChange={(e) => {
                      setValue("companyName", e.target.value);
                    }}
                    displayEmpty
                    style={{ height: "38px", width: "240px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {organizations.map((item) => (
                      <MenuItem key={item.id} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="flex flex-col w-[32%]">
            <div className="flex items-center">
              <label className="font-bold mr-3 w-[35%] shrink-0 text-right">Participant Type</label>
              <Controller
                control={control}
                name="participantType"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="participantType"
                    value={value ?? ""}
                    onChange={(e) => {
                      setValue("participantType", e.target.value);
                    }}
                    displayEmpty
                    style={{ height: "38px", width: "200px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {PARTICIPANT_TYPES.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="flex flex-col w-[32%]">
            <TextInput
                label="User Name"
                value=""
                register={{ ...register("fullName"), className: "w-full" }}
                labelClassName="mr-4 w-[30%] shrink-0 text-right font-bold"
              />
          </div>

          <div className="flex flex-col w-[32%]">
            <div className="flex items-center">
              <label className="mr-4 w-[30%] shrink-0 text-right font-bold">Role</label>
              <Controller
                control={control}
                name="role"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    id="role"
                    value={value ?? ""}
                    onChange={(e) => {
                      setValue("role", e.target.value);
                    }}
                    displayEmpty
                    style={{ height: "38px", width: "240px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={"Admin"}>Admin</MenuItem>
                    <MenuItem value={"Super Admin"}>Super Admin</MenuItem>
                    <MenuItem value={"User"}>User</MenuItem>
                  </Select>
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-center justify-center mt-10 gap-5">
          <ButtonCancelA
            name="Reset"
            onHandleClick={() => {
              reset({ parentCompanyId: "", companyName: "", participantType: "", fullName: "", role: "" });
              setUserSearch({ companyName: "", fullName: "", role: "", parentCompanyId: "", participantType: "" });
              setPage(0);
              if (isRoleSupperAdmin) {
                setOrganizations([]);
              } else {
                mutationGetOrganizations.mutate(callerParentCompanyId);
              }
              onAtomReset?.();
            }}
            icon={<ClearIcon />}
          />
          <SearchButton />
        </div>
      </form>
    </div>
  );
};
